import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Breadcrumbs, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/hooks';
import { ThunkGetTicketTemplates } from 'store/ticketTemplates/thunks';
import { PortalConfigSelector } from 'store/config/selectors';

import { TICKETS } from 'router/routes';
import { ITicketTemplate } from 'services/ticketTemplates/types';

import Loader from 'components/Loader';
import CreateTicketTemplateItem from './components/CreateTicketTemplateItem';
import CreateTicketForm from './components/CreateTicketForm';

import useStyles from './styles';
import { tt } from '@i18n/tt';

interface IProps {
  useTeamsStyles?: boolean;
}

const CreateTicket: React.FC<IProps> = ({ useTeamsStyles = false }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [ticketTemplates, setTicketTemplates] = useState<ITicketTemplate[]>();

  const { defaultTicketTemplateName, useDefaultTicketTemplate } = useSelector(PortalConfigSelector);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const classes = useStyles();

  const handleRedirectToTickets = useCallback(() => {
    navigate(TICKETS);
  }, [navigate]);

  const defaultTemplate: ITicketTemplate = useMemo(
    () => ({
      id: 0,
      title: defaultTicketTemplateName,
      type: 2,
      questions: [],
      value: 'Other',
    }),
    [defaultTicketTemplateName],
  );

  const onMount = useCallback(() => {
    setLoading(true);
    dispatch(ThunkGetTicketTemplates())
      .unwrap()
      .then(res => {
        setTicketTemplates(
          useDefaultTicketTemplate ? [...res, defaultTemplate] : res
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    onMount();
  }, [onMount]);

  if (loading) return <Loader />;
  return (
    <div className={useTeamsStyles ? classes.createTicketTeams : classes.createTicket}>
      <div className={classes.createTicketHeader}>
        <Breadcrumbs>
          <div onClick={handleRedirectToTickets} role="presentation" className="link">
            {tt('pages.ticket.breadcrumbs.tickets')}
          </div>

          <div>{tt('pages.ticket.breadcrumbs.createTicket')}</div>
        </Breadcrumbs>
        <div>
          <Button variant="contained" onClick={handleRedirectToTickets}>
            {tt('buttons.back')}
          </Button>
        </div>
      </div>

      {ticketTemplates && ticketTemplates.map(i => <CreateTicketTemplateItem {...i} key={i.id} />)}
      {ticketTemplates?.length === 1 && <CreateTicketForm />}
    </div>
  );
};

export default CreateTicket;
